<template>
    <div class="creatives">
        <div class="creatives__header">
            <div class="creatives__titlebar">
                <h2 class="creatives__title">Guaranteed креативы</h2>
                <button
                    class="update-btn"
                    :class="{ 'update-btn--disabled': syncing, 'update-btn--spinning': syncing }"
                    :disabled="syncing"
                    @click="syncStatuses"
                    aria-label="Синхронизировать креативы"
                    :title="'isAdmin: ' + isAdmin + ', user: ' + JSON.stringify(user)"
                ></button>
            </div>
            <div class="creatives__meta">
                <input
                    v-model.trim="query"
                    type="text"
                    class="input input--search"
                    placeholder="Поиск по названию"
                    aria-label="Поиск по названию"
                />
                <select v-model="statusFilter" class="select" aria-label="Фильтр по статусу">
                    <option value="">Все статусы</option>
                    <option v-for="(label, key) in statusDict" :key="key" :value="key">
                        {{ label }}
                    </option>
                </select>
                <select v-model="typeFilter" class="select" aria-label="Фильтр по типу">
                    <option value="">Все типы</option>
                    <option v-for="(label, key) in typeDict" :key="key" :value="key">
                        {{ label }}
                    </option>
                </select>
                <span class="muted">Всего: {{ filteredCreatives.length }}</span>
            </div>
        </div>

        <div v-if="(creatives || []).length === 0" class="creatives__empty">Нет креативов</div>

        <div v-else class="creatives__flex">
            <div class="creative-card" v-for="creative in filteredCreatives" :key="creative.id">
                <div class="creative-card__preview">
                    <video
                        v-if="isVideo(creative)"
                        :src="creative.url"
                        controls
                        preload="metadata"
                        playsinline
                    ></video>
                    <img v-else :src="creative.url" alt="preview" />
                </div>
                <div class="creative-card__body">
                    <div class="creative-card__row creative-card__row--top">
                        <div class="creative-card__title" :title="creative.name">
                            <img 
                                :src="getTypeIcon(creative)" 
                                :alt="creative.type"
                                class="title-type-icon"
                            />
                            <a :href="creative.url" target="_blank">{{ creative.name }}</a>
                        </div>
                        <div class="creative-card__actions">
                            <button
                                class="btn btn--danger btn--icon"
                                @click="confirmDelete(creative)"
                                :disabled="isDeletingId === creative.id || isChangingStatusId === creative.id"
                                aria-label="Удалить"
                                title="Удалить контент"
                            ></button>
                        </div>
                    </div>
                    <div class="creative-card__row creative-card__meta">
                        <div class="status-control">
                            <select
                                v-if="isAdmin"
                                :value="creative.status"
                                @change="changeStatus(creative, $event.target.value)"
                                class="status-select"
                                :class="'status-select--' + normalizeStatus(creative.status)"
                            >
                                <option value="approve">approve</option>
                                <option value="review">review</option>
                                <option value="confirmed">confirmed</option>
                                <option value="rejected">rejected</option>
                                <option value="cancelled">cancelled</option>
                            </select>
                            <span
                                v-else
                                :class="['status', 'status--' + normalizeStatus(creative.status)]"
                            >
                                <span class="status__text">{{ statusLabel(creative.status) }}</span>
                            </span>
                        </div>
                        <span class="dot">•</span>
                        <span class="meta">ID: {{ creative.id }}</span>
                        <span class="dot">•</span>
                        <span v-if="hasDimensions(creative)" class="meta meta--size">
                            <img 
                                src="/images/size.svg" 
                                alt="Размер"
                                class="size-icon"
                            />
                            {{ formatDimensions(creative) }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="showConfirm" class="modal-backdrop" role="dialog" aria-modal="true">
            <div class="modal">
                <div class="modal__title">Удалить контент?</div>
                <div class="modal__body">
                    Это действие необратимо. Контент будет удален через API модерации и из базы данных.
                </div>
                <div class="modal__actions">
                    <button class="btn" @click="cancelModal">Отмена</button>
                    <button class="btn btn--danger" @click="doDelete">Удалить</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import { useToast } from '../../composables/useToast';
import { notifyHttpError } from '../../utils/httpErrors';
import { mapCampaignStatus, getCampaignStatusLabel } from '../../utils/campaignStatus.js';

export default {
    name: 'GuaranteedCreatives',
    props: {
        creatives: {
            type: Array,
            required: true,
        },
        user: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            query: '',
            statusFilter: '',
            typeFilter: '',
            showConfirm: false,
            creativeToDelete: null,
            isDeletingId: null,
            isChangingStatusId: null,
            syncing: false,
            // Упрощённые статусы как у кампаний (для фильтра и отображения не-админу)
            statusDict: {
                active: 'Active',
                paused: 'Paused',
                finished: 'Finished',
                moderation: 'Moderation',
            },
            typeDict: {
                picture: 'Изображение',
                video: 'Видео',
            },
        };
    },
    computed: {
        isAdmin() {
            return this.user?.is_admin === true;
        },
        creativesSafe() {
            return Array.isArray(this.creatives) ? this.creatives : [];
        },
        filteredCreatives() {
            const q = (this.query || '').toLowerCase();
            const status = (this.statusFilter || '').toLowerCase();
            const type = (this.typeFilter || '').toLowerCase();
            return this.creativesSafe.filter((cr) => {
                const nameOk = q
                    ? String(cr.name || '')
                          .toLowerCase()
                          .includes(q)
                    : true;
                const statusOk = status ? mapCampaignStatus(cr.status) === status : true;
                const typeOk = type
                    ? this.normalizeType(cr.type) === type || (type === 'video' && this.isVideo(cr))
                    : true;
                return nameOk && statusOk && typeOk;
            });
        },
    },
    methods: {
        async syncStatuses() {
            if (this.syncing) return;
            this.syncing = true;
            try {
                await axios.post('/api/creatives/sync-statuses');
                useToast().success('Статусы обновлены');
            } catch (e) {
                notifyHttpError(e, 'Не удалось обновить статусы');
            } finally {
                this.syncing = false;
            }
        },
        normalizeStatus(s) {
            if (!s) return 'moderation';
            if (this.isAdmin) return String(s).toLowerCase();
            return mapCampaignStatus(s);
        },
        normalizeType(t) {
            const k = String(t || '').toLowerCase();
            if (k.startsWith('image')) return 'picture';
            if (k.startsWith('video')) return 'video';
            return k;
        },
        isVideo(creative) {
            if (!creative) return false;
            const type = (creative.type || '').toString().toLowerCase();
            if (type.includes('video')) return true;
            const url = (creative.url || '').toString().toLowerCase();
            return /(\.mp4|\.webm|\.ogg|\.ogv|\.mov|\.m4v)(\?.*)?$/.test(url);
        },
        hasDimensions(creative) {
            const w = Number(creative && creative.width);
            const h = Number(creative && creative.height);
            return Number.isFinite(w) && Number.isFinite(h) && w > 0 && h > 0;
        },
        formatDimensions(creative) {
            const w = Number(creative.width);
            const h = Number(creative.height);
            return `${w}×${h}`;
        },
        statusLabel(s) {
            if (this.isAdmin) return String(s ?? '');
            return getCampaignStatusLabel(mapCampaignStatus(s));
        },
        getTypeIcon(creative) {
            if (this.isVideo(creative)) {
                return '/images/video.svg';
            }
            return '/images/picture.svg';
        },
        confirmDelete(creative) {
            this.creativeToDelete = creative;
            this.showConfirm = true;
        },
        async doDelete() {
            if (!this.creativeToDelete) return;
            try {
                this.isDeletingId = this.creativeToDelete.id;
                await axios.delete(`/api/creatives/${this.creativeToDelete.id}`);
                const idx = (this.creatives || []).findIndex(
                    (x) => x.id === this.creativeToDelete.id
                );
                if (idx !== -1) {
                    this.creatives.splice(idx, 1);
                }
                useToast().success('Контент удалён');
            } catch (e) {
                notifyHttpError(e, 'Не удалось удалить контент');
            } finally {
                this.isDeletingId = null;
                this.showConfirm = false;
                this.creativeToDelete = null;
            }
        },
        async changeStatus(creative, newStatus) {
            if (!creative || !newStatus) return;
            if (creative.status === newStatus) return;
            
            try {
                this.isChangingStatusId = creative.id;
                await axios.post(`/api/creatives/${creative.id}/change-status`, { status: newStatus });
                const idx = (this.creatives || []).findIndex(
                    (x) => x.id === creative.id
                );
                if (idx !== -1) {
                    this.creatives[idx].status = newStatus;
                }
                useToast().success('Статус обновлён');
            } catch (e) {
                notifyHttpError(e, 'Не удалось изменить статус');
                this.$forceUpdate();
            } finally {
                this.isChangingStatusId = null;
            }
        },
        cancelModal() {
            this.showConfirm = false;
            this.creativeToDelete = null;
        },
    },
};
</script>

<style scoped>
.title-type-icon {
    width: 18px;
    height: 18px;
    margin-right: 8px;
    vertical-align: middle;
    opacity: 0.8;
}

.size-icon {
    width: 16px;
    height: 16px;
    margin-right: 4px;
    vertical-align: middle;
    opacity: 0.7;
}

.meta--size {
    display: inline-flex;
    align-items: center;
}

.creative-card__title {
    display: flex;
    align-items: center;
}

.status-control {
    display: inline-block;
}

.status-select {
    padding: 4px 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 12px;
    background: white;
    cursor: pointer;
    transition: all 0.2s;
}

.status-select:hover {
    border-color: #999;
}

.status-select:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: 0 0 0 2px rgba(52, 152, 219, 0.2);
}

.status-select--approve {
    background-color: #d4edda;
    border-color: #c3e6cb;
    color: #155724;
}

.status-select--review {
    background-color: #fff3cd;
    border-color: #ffeaa7;
    color: #856404;
}

.status-select--confirmed {
    background-color: #d1ecf1;
    border-color: #bee5eb;
    color: #0c5460;
}

.status-select--rejected {
    background-color: #f8d7da;
    border-color: #f5c6cb;
    color: #721c24;
}

.status-select--cancelled {
    background-color: #e2e3e5;
    border-color: #d6d8db;
    color: #383d41;
}

.creative-card__actions {
    display: flex;
    gap: 8px;
}
</style>

